import config from 'config'

import { AbstractService, LoggerParameter } from 'shared/services/AbstractService'
import { ValidationError } from 'shared/services/errors'
import { getLogger } from 'shared/logger'
import { parseWorkEntity } from '~/app/utils/parseWorkEntity'

class WesServiceApi extends AbstractService implements VP.DTT.Services.WorkEntityService.IWesService {
  apiVersion: number

  constructor({ version, ...rest }: Gallery.Config.IServiceConfig, logger: LoggerParameter) {
    super(rest, logger)
    this.apiVersion = version
  }

  /**
   * Add favorite to WES
   * @param wesTenant
   * @param favorite
   * @param ownerId
   * @param authorizationHeader
   */
  public async addFavorite(
    wesTenant: string,
    favorite: VP.DTT.Models.WorkEntityService.WorkEntity<string>,
    ownerId?: string,
    authorizationHeader?: string,
  ): Promise<string> {
    const url = `api/${this.apiVersion}/works`

    if (ownerId === undefined || authorizationHeader === undefined) {
      throw new ValidationError('Unable to call addFavorite, no ownerId and/or authorizationHeader provided', url, { favorite })
    }

    const { data } = await this.api.post<VP.DTT.Models.WorkEntityService.WorkEntity<string>>(url, favorite, {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        ownerId,
        tenant: wesTenant,
        from: config.appName,
      },
    })

    // TODO throw error here if we get a bad response from WES
    return data.workId || '0'
  }

  /**
   * Remove favorite from WES
   * @param favoriteId
   * @param authorizationHeader
   */
  public async removeFavorite(favoriteId: string, authorizationHeader?: string): Promise<number> {
    const url = `api/${this.apiVersion}/works/${favoriteId}/delete`

    if (authorizationHeader === undefined) {
      throw new ValidationError('Unable to call removeFavorite, no authorizationHeader provided', url, { favoriteId })
    }

    const { status } = await this.api.delete(url, {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        from: config.appName,
      },
    })

    return status
  }

  /**
   * Set favorite visibility in WES
   * @param favoriteId
   * @param isVisible
   * @param authorizationHeader
   */
  public async setFavoriteVisibility(favoriteId: string, isVisible: boolean, authorizationHeader?: string): Promise<number> {
    const url = `api/${this.apiVersion}/works/${favoriteId}/setVisible`

    if (authorizationHeader === undefined) {
      throw new ValidationError('Unable to call setVisible, no authorizationHeader provided', url, { favoriteId })
    }

    const { status } = await this.api.patch<number>(
      url,
      { hidden: isVisible },
      {
        headers: {
          Authorization: authorizationHeader,
        },
        params: {
          from: config.appName,
        },
      },
    )

    return status
  }

  /**
   * Get favorite from WES
   * @param favoriteId
   * @param authorizationHeader
   */
  public async getFavorite(
    favoriteId: string,
    authorizationHeader?: string | null,
  ): Promise<VP.DTT.Models.WorkEntityService.WorkEntity<Gallery.ContentQuery.PreviewInfo | null>> {
    const url = `api/${this.apiVersion}/works/${favoriteId}`

    if (!authorizationHeader) {
      throw new ValidationError('Unable to call getFavorite, no authorizationHeader provided', url, { favoriteId })
    }

    const { data } = await this.api.get<VP.DTT.Models.WorkEntityService.WorkEntity<string>>(url, {
      headers: {
        Authorization: authorizationHeader,
      },
      params: {
        from: config.appName,
      },
    })

    return parseWorkEntity(data)
  }

  /**
   * Gets a page of favorites from WES, optionally filtered by gallery/productKey
   * @param locale
   * @param pageSize
   * @param pageNumber
   * @param wesTenant
   * @param ownerId
   * @param authorizationHeader
   * @param productKey
   * @param includeHidden
   */
  public async getFavoritesPage(
    locale: string,
    pageSize: number,
    pageNumber: number,
    wesTenant: string,
    ownerId?: string,
    authorizationHeader?: string | null,
    productKey?: string,
  ): Promise<VP.DTT.Models.WorkEntityService.WorkEntity<Gallery.ContentQuery.PreviewInfo>[]> {
    const url = 'api/v2/works:search'

    if (!ownerId || !authorizationHeader) {
      throw new ValidationError('Unable to call getFavoritesPage, no ownerId and/or authorizationHeader provided', url)
    }

    const { data } = await this.api.get<VP.DTT.Models.WorkEntityService.WorkEntity<string>[]>(url, {
      headers: {
        Authorization: authorizationHeader,
        'Cache-Control': 'max-age=0, no-cache, no-store',
      },
      params: {
        tenants: wesTenant,
        pageSize,
        pageNumber,
        ownerId,
        workName: locale,
        sortBy: 'LastCreated',
        from: config.appName,
        productKey: productKey || undefined,
        searchMode: 'Exclusive',
      },
    })

    return data.map((favorite: VP.DTT.Models.WorkEntityService.WorkEntity<string>) => parseWorkEntity(favorite))
  }

  /**
   * Get total favorites count from WES
   * @param tenant
   * @param ownerId
   * @param authorizationHeader
   */
  public async getFavoritesCount(
    tenant: string,
    ownerId?: string,
    authorizationHeader?: string | null,
    productKey?: string,
  ): Promise<VP.DTT.Models.WorkEntityService.CountResponse> {
    const url = 'api/v2/works/filteredCount'

    if (!ownerId || !authorizationHeader) {
      throw new ValidationError('Unable to call getFavoritesCount, no ownerId and/or authorizationHeader provided', url)
    }

    const { data } = await this.api.get<VP.DTT.Models.WorkEntityService.CountResponse>(url, {
      headers: {
        Authorization: authorizationHeader,
        'Cache-Control': 'max-age=0, no-cache, no-store',
      },
      params: {
        tenants: tenant,
        ownerId,
        from: config.appName,
        productKey,
      },
    })

    return data
  }

  public async getAllFavorites(
    locale: string,
    wesTenant: string,
    ownerId?: string,
    authorizationHeader?: string | null,
    productKey?: string,
  ): Promise<VP.DTT.Models.WorkEntityService.WorkEntity<Gallery.ContentQuery.PreviewInfo>[]> {
    const pageSize = 50
    const numFavorites = (await this.getFavoritesCount(wesTenant, ownerId, authorizationHeader, productKey)).ownedWorks

    let allFavorites = <VP.DTT.Models.WorkEntityService.WorkEntity<Gallery.ContentQuery.PreviewInfo>[]>[]

    const pages = numFavorites <= 100 ? Array.from(Array(Math.ceil(numFavorites / pageSize)).keys()) : [0, 1]

    const promises = pages.map((page) => this.getFavoritesPage(locale, pageSize, page, wesTenant, ownerId, authorizationHeader, productKey))

    const pagedFavorites = await Promise.all(promises)

    allFavorites = pagedFavorites.flat()

    const favorites = allFavorites?.map((obj) => ({ ...obj, isFavorite: true }))

    return favorites
  }
}

export const WesService = new WesServiceApi(config.services.wesService, getLogger)
